import {
  Box,
  Center,
  Code,
  Flex,
  Image,
  Spinner,
  Text,
  useClipboard,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from 'src/components/atoms/Button';

type TotpEnrollmentProps = {
  setupUri: string | null;
  secret: string | null;
};

/**
 * Presentational block for registering an authenticator app:
 * QR code to scan plus the shared secret for manual entry.
 * Used in both the sign-in TOTP setup step and the Settings modal.
 */
export const TotpEnrollment: FC<TotpEnrollmentProps> = ({
  setupUri,
  secret,
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const { onCopy, hasCopied } = useClipboard(secret ?? '');

  useEffect(() => {
    let cancelled = false;
    setQrDataUrl(null);
    if (setupUri) {
      QRCode.toDataURL(setupUri, { width: 200, margin: 1 }).then((url) => {
        if (!cancelled) {
          setQrDataUrl(url);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [setupUri]);

  return (
    <Box>
      <Text mb={4}>
        Scan this QR code with your authenticator app (e.g. Google Authenticator
        or Microsoft Authenticator):
      </Text>
      <Center mb={4}>
        {qrDataUrl ? (
          <Image
            src={qrDataUrl}
            alt="Authenticator app QR code"
            width="200px"
            height="200px"
          />
        ) : (
          <Spinner />
        )}
      </Center>
      {secret && (
        <Box mb={4}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Can&apos;t scan the code? Enter this key manually:
          </Text>
          <Flex alignItems="center" gap={2}>
            <Code
              px={2}
              py={1}
              borderRadius="md"
              wordBreak="break-all"
              flex="1">
              {secret}
            </Code>
            <Button
              buttonName="copy-totp-secret"
              variant="outline"
              size="sm"
              onClick={onCopy}>
              {hasCopied ? 'Copied' : 'Copy'}
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};
